/**
 * Component for managing Litmus interaction drafts and versions
 * Handles version listing, publishing, and restoration of conversation configurations
 */
import { Component, DestroyRef, inject } from '@angular/core';
import { NgFor, NgIf, CommonModule  } from '@angular/common';
import * as _ from 'lodash';
import { VerbatimService } from 'src/app/shared/services/verbatim/verbatim.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HelpersService } from 'src/app/shared/services/helpers/helpers.service';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AccountsService } from 'src/app/shared/services/accounts/accounts.service';
import { retry } from 'rxjs/operators';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import { ConversationService } from 'src/app/shared/services/conversation/conversation.service';

@Component({
    selector: 'app-litmus-intraction-draft',
    imports: [NgIf, NgFor, FormsModule, LtmsLoaderComponent, CommonModule ],
    templateUrl: './litmus-intraction-draft.component.html',
    styleUrl: './litmus-intraction-draft.component.scss'
})
export class LitmusIntractionDraftComponent {
  destroyRef = inject(DestroyRef);
  versionList: any[] = [];
  brandDetails = Helpers.brandDetails;
  brandOffsetName = {};
  columnName: any[] = [];
  pageNumber: number = 1;
  isDataAvailable: boolean = false;
  isProjectLevelConversationPreview = false;
  currentPublishedVersion = null;
  conversationPreviewURL = '';
  isLoading: boolean = false;
  selectedRow: number | null = null;
  liveVersionDetails: any = {};
  totalVersionListCount: number = 0;

  totalPages: number = 0
  hasNextPage: boolean = true;

  alertMessages: string[] = []; // Array of messages for the alert modal body
  alertCancelText: string = 'Cancel'; // Text for the cancel button in the alert
  alertConfirmText: string = 'Confirm'; // Text for the confirm button in the alert
  pendingAction: { type: 'publish' | 'restore', version: string } | null = null; // To store the action to be performed after confirmation

  // Properties for the custom alert modal
  showAlert: boolean = false;
  alertTitle: string = '';
  alertMessage = '';
  currentVersionToActOn: any = null; // To store the version being acted upon
  currentAction: 'publish' | 'restore' | null = null; // To store the action type

  isTableLoading: boolean = false;

  searchQuery: string = '';
  errorMessage: string = '';
  private versionRegex = /^\d+\.\d+\.\d+$/;
  latestVersionNumber = null;
  isUserHasWriteAccess: boolean = false;
  constructor(private verbatimService: VerbatimService,
    private helpersService: HelpersService,
    private toastService: ToastService,
    private accountsService: AccountsService,
    private _conversation: ConversationService
  ) {
    this.defaultLoadingAllDetails();
    this.isUserHasWriteAccess = this._conversation.isUserHasWriteAccess('MANAGE_PUBLISH');
  }

  defaultLoadingAllDetails() {
    this.brandOffsetName = this.helpersService.getBrandTimezoneDetails(this.brandDetails);
    this.currentPublishedVersion = _.get(Helpers.brandDetails, 'current_configuration.100');
    this.getLiveVersionDetails(this.currentPublishedVersion);
    if (this.brandDetails && this.brandDetails['help_resources'] && this.brandDetails['help_resources'].length > 0) {
      const list = this.brandDetails['help_resources'].filter((ele: { _id: string; }) => ele._id == 'preview_v2_link');
      if (list && list.length > 0) {
        this.isProjectLevelConversationPreview = true;
        this.conversationPreviewURL = list[0]['url'];
      }
    }
    this.getBrandConversationConfigList();
  }

  getLiveVersionDetails(liveVersion) {
    if (liveVersion) {
      this.getBrandConfigByVersion(liveVersion, (response: any) => {
        this.liveVersionDetails['version'] = liveVersion;
        this.liveVersionDetails['published'] = this.helpersService.getTimeByOffset(this.brandOffsetName["__zone_symbol__value"]['offset'], _.get(response, 'data.brand_configuration.0.created_date'), "DD-MMM-YY HH:mm");
        this.liveVersionDetails['published_by'] = _.get(response, 'data.brand_configuration.0.created_by');
        if (_.get(response, 'data.brand_configuration.0.created_by_email_address')) {
          this.liveVersionDetails['published_by'] = _.get(response, 'data.brand_configuration.0.created_by_email_address');
        }
        if (_.get(response, 'data.brand_configuration.0.reference_version')) {
          this.liveVersionDetails['created_from'] = _.get(response, 'data.brand_configuration.0.reference_version');
        }
      })
    }
  }

  onRestoreAndPublish(index: number, versionNumber: string) {
  this.selectedRow = index;
  this.publishSelectedVersion(versionNumber);
}

  /**
   * Fetches detailed configuration for a specific version
   * @param version - Version number to fetch
   * @param cb - Callback function to handle the response
   */
  getBrandConfigByVersion(version, cb) {
    let params = {};
    params['version'] = version;
    params['brand_id'] = Helpers.brandDetails._id;
    this.verbatimService.getConversationDetailedByVersion(params).pipe(
      retry(2),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      cb(data)
    });
  }

  getBrandConversationConfigList(event_type?: string) {
    let params = {};
    params['page_number'] = this.pageNumber;
    params['limit'] = 10;
    params['brand_id'] = Helpers.brandDetails._id;
    if (event_type == 'page_move') {
      this.isTableLoading = true;
    } else {
      this.isLoading = true;
    }
    this.verbatimService.getConversationVersionList(params).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (data) => {
        this.errorMessage = ''; // Clear any previous error
        const isDataAvailable = (_.get(data, "data.code") == 0) ? true : false;
        const list = _.get(data, "data.version_list");
        if (isDataAvailable && list && list.length > 0) {
          if (list[0] && this.totalVersionListCount == 0) {
            this.totalVersionListCount = Number(list[0]['version'].split('.').pop()) + 1;
            this.totalPages = Math.ceil(Number(this.totalVersionListCount) / 10);
            this.latestVersionNumber = list[0]['version'];
          }
          // Check if the number of items is less than the limit, implies no next page
          if (list.length < 10) {
            this.hasNextPage = false;
          } else {
            this.hasNextPage = true;
          }
          this.prepareData(list, data['data']['manager_list'], event_type);
        } else {
          if (list.length < 10) {
            this.hasNextPage = false;
          } else {
            this.hasNextPage = true;
          }
          if (event_type == 'page_move') {
            this.isTableLoading = false;
          } else {
            this.isLoading = false;
          }
        }
      },
      error: (error) => {
        console.error('Error fetching conversation list:', error);
        this.versionList = [];
        this.isDataAvailable = false;
        this.errorMessage = 'Failed to load versions. Please try again.';
        if (event_type == 'page_move') {
          this.isTableLoading = false;
        } else {
          this.isLoading = false;
        }
      }
    });
  }

  prepareData(configList, managers, event_type) {
    this.versionList = [];
    // Enrich version list with manager information
    const enrichedVersionList = configList.map(function (v) {
      const manager = _.find(managers, { _id: v.created_by });
      return Object.assign({}, v, {
        created_by: manager ? manager.name : v.created_by
      });
    });
    // Process each version and add status information
    enrichedVersionList.forEach(config => {
      if (config) {
        const data = {}
        data['version'] = config['version'];
        data['created_date'] = this.helpersService.getTimeByOffset(this.brandOffsetName["__zone_symbol__value"]['offset'], config['created_date'], "DD-MMM-YY HH:mm");
        data['created_by'] = config['created_by'];
        // if (this.isProjectLevelConversationPreview) {
          // const previewURL = this.conversationPreviewURL + '?config=' + config['version'];
          const previewURL = `interaction/preview?project_id=${this.brandDetails['_id']}&version=${config['version']}`
          data['preview'] = '<a  href="' + previewURL + '" target="_blank" >Click Here</a>'
        // }
        if (config['reference_version']) {
          data['source_version'] = config['reference_version'];
        }
        data['status'] = ''
        if (this.currentPublishedVersion) {
          if (config['version'] == this.currentPublishedVersion) {
            data['status'] = 'Live'
          }
        }
        this.versionList.push(data);
      }
    });
    if (event_type == 'page_move') {
      this.isTableLoading = false
    } else {
      this.isLoading = false;
    }
    this.isDataAvailable = true;
    setTimeout(() => {
      this.mapLiveConversationPublishManager(managers);
    }, 1000);
  }

  // temporary code for manager details.
  mapLiveConversationPublishManager(manager_list) {
    if (this.liveVersionDetails) {
      if (this.liveVersionDetails['published_by']) {
        let manager_details = _.find(manager_list, { _id: this.liveVersionDetails['published_by'] });
        if (manager_details) {
          this.liveVersionDetails['published_by'] = manager_details['name'] ? manager_details['name'] : manager_details['user_name']
        }
      }
    }
  }


  /**
   * Handles page navigation based on direction
   * @param pagingType - Direction of navigation ('next' or 'previous')
   */
  pageMove(direction: 'previous' | 'next'): void {
    if (direction === 'previous' && this.pageNumber > 1) { 
      this.pageNumber--;
      this.getBrandConversationConfigList('page_move');
    } else if (direction === 'next') {
      if (this.pageNumber < this.totalPages) {
        this.pageNumber++;
        this.getBrandConversationConfigList('page_move');
      }
    }
  }

  /**
   * Publishes a selected version as the live configuration
   * @param version - Version number to publish
   */
  publishSelectedVersion(version: string): void {
    this.alertTitle = 'Restore & Publish Version';
    this.alertMessages = [
      `Restore & Publish Action:`,
      `This will:<br>1. Load version <strong>${version}</strong> into your conversation editor<br>2. Immediately make this version live - replacing your current published conversation`,
      `<strong>Warning:</strong> New respondents will see version <strong>${version}</strong> right away. This action cannot be undone easily.`,
    ];
    this.alertCancelText = 'Cancel';
    this.alertConfirmText = 'Restore & Publish';
    this.pendingAction = { type: 'publish', version: version };
    this.showAlert = true;
  }
  /**
   * Restores a selected version without publishing
   * @param version - Version number to restore
   */
  restoreSelectedVersion(version: string, index: number): void {
    this.alertTitle = 'Restore Version';
    this.alertMessages = [
      `Restore Action:`,
      `This will load version <strong>${version}</strong> into your conversation editor for further modifications.`,
      `<strong>Important:</strong> Your currently published live conversation will remain unchanged. Respondents will continue to see the current live version until you explicitly publish a new version.`,
    ];
    this.alertCancelText = 'Cancel';
    this.alertConfirmText = 'Restore to Editor';
    this.pendingAction = { type: 'restore', version: version };
    this.showAlert = true;
     this.selectedRow = index;
  }


  /**
   * Handles the cancel action from the custom alert modal.
   * Simply hides the modal.
   */
  onAlertCancel(): void {
    this.showAlert = false;
    this.pendingAction = null; // Reset pending action
    this.selectedRow = null;
  }
  /**
   * Hides the custom alert modal and resets related properties.
   */
  hideAlertModal() {
    this.showAlert = false;
    this.alertTitle = '';
    this.alertMessage = '';
    this.currentVersionToActOn = null;
    this.currentAction = null;
  }

  /**
   * Handles the confirmation action from the custom alert modal.
   * Proceeds with the stored publish or restore action.
   */
  onAlertConfirm(): void {
    if (this.pendingAction) {
      if (this.pendingAction?.type === 'publish') {
        this.postConversationBrandConfig(this.pendingAction.version, true);
      } else if (this.pendingAction?.type === 'restore') {
        this.postConversationBrandConfig(this.pendingAction.version, false);
      }
      this.showAlert = false; // Hide modal
      this.pendingAction = null; // Reset pending action
      this.selectedRow = null;
    }
  }



  /**
   * Posts conversation configuration changes to the server
   * Handles both publishing and restoration of versions
   * @param version - Version number to update
   * @param isPublish - Flag indicating if the version should be published
   */
  postConversationBrandConfig(version, isPublish) {
    this.isLoading = true;
    this.getBrandConfigByVersion(version, (response) => {
      const isDataAvailable = (_.get(response, "data.code") == 0) ? true : false;
      if (isDataAvailable) {
        if (response.data.message.toLowerCase() == 'success') { 
          let postObject = {}
          // Prepare brand configuration object
          var configData = response.data.brand_configuration[0];
          configData['reference_version'] = version;
          delete configData._id;
          configData['created_by'] = _.get(this.helpersService.helpers, 'managerDetails.manager._id');
          configData['created_by_email_address'] = _.get(this.helpersService.helpers, 'managerDetails.manager.email_address');
          // Prepare page_view_details if configuration being publish
          if(isPublish) {
            let pageViewDetails = this.handlePages(configData);
            if (pageViewDetails && pageViewDetails.length > 0) {
              configData['page_view_details'] = pageViewDetails;  
            }
          }
          // Prepare post object with configuration and publish status
          postObject['brand_config'] = configData;
          postObject['is_publish'] = isPublish;
          postObject['brand_id'] = Helpers.brandDetails._id;
          // Submit configuration changes to server
          this.verbatimService.postConversationConfig(postObject).pipe(
            takeUntilDestroyed(this.destroyRef)
          ).subscribe((response) => {
            if (!response.error) {
              if (response.code == 403) {
                this.toastService.showPopup('ERROR', 'Error', "Unauthorized", 6000);
              } else if (response.code == 0) {
                this.pageNumber = 1;
                // Refresh the version list after successful update
                this.afterUpdatePublishVersion();      
                if (isPublish) {
                  this.toastService.showPopup('SUCCESS', 'Success', "Version restored & published successfully!", 6000);
                } else {
                  this.toastService.showPopup('SUCCESS', 'Success', "Version restored successfully!", 6000);
                }
              } else {
                this.toastService.showPopup('ERROR', 'Error', "Something went wrong", 6000);
              }
            } else {
              this.toastService.showPopup('ERROR', 'Error', "Something went wrong", 6000);
            }
          });
        } else {
          this.isLoading = false;
          console.log("Failed: ", response)
        }
      } else {
        this.isLoading = false;
        console.log("Issue with response: ", response)
      }
    })
  }

  // Added by Bhunesh - Feb 12 2017 - handlePages() To handle if pageid is exist in flow but not defined in screens
  handlePages(configuration) {
    // Added by Bhunesh - Feb 17 2017 - Save Page view details for dashboard analytics usage
    var pageViewDetails: any = [];
    var pageHash: any = {};
    var devices = ["phone", "desktop", "tablet"];
    var default_language = configuration.default_language;
    _.each(devices, (device) => {
      try {
        var groups = configuration["flows"][device.toString()][default_language];
        // filter pages which are defined on screens
        groups = _.filter(groups, (group) => {
          var page = this.getPageDetailById(group, device, default_language, configuration);
          if (page) {
            pageHash[page.id] = page;
          }
          return !!page;
        });
        // saving page view details
        _.each(groups, (group, index) => {
          if (pageHash[group]) {
            pageViewDetails.push({
              "page_name": pageHash[group].id,
              "device": device,
              "display_name": pageHash[group].page_title,
              "page_sequence": index + 1
            })
          }
        });
        configuration["flows"][device.toString()][default_language] = groups;
      } catch (e) {
        console.error("Error: Conversation Designer: handlePages()", e);
      }
    })
    return pageViewDetails;
  }

  getPageDetailById(pageId, deviceId, default_language, configuration) {
    try {
      if (configuration["screens"][deviceId.toString()] && configuration["screens"][deviceId.toString()][default_language]) {
        return _.find(configuration["screens"][deviceId.toString()][default_language], { id: pageId });
      }
    } catch (e) {
      console.error("Error: Conversation Designer: getPageDetailById()", e);
      return null;
    }

  }

  afterUpdatePublishVersion() {
    this.accountsService.getBrandDetail(this.brandDetails._id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response) => {
      if (response.code === 0 && response.data.brand) {
        this.totalVersionListCount = 0;
        this.brandDetails = response.data.brand;
        this.helpersService.setInitialData({}, {}, {}, this.brandDetails)
        this.currentPublishedVersion = _.get(this.brandDetails, 'current_configuration.100');
        this._conversation.getConfiguration(this.brandDetails._id, false);
        this.getLiveVersionDetails(this.currentPublishedVersion);
        this.getBrandConversationConfigList();
      }
    });
  }

  onSearch() {
    if (!this.searchQuery.trim()) {
      // If search is empty, reset to normal list
      this.pageNumber = 1;
      this.getBrandConversationConfigList();
      return;
    }

    // Validate version format
    if (!this.versionRegex.test(this.searchQuery)) {
      this.errorMessage = 'Please enter a valid version number (e.g., 1.0.1)';
      return;
    }

    this.isTableLoading = true;
    this.errorMessage = '';

    // Call getBrandConfigByVersion with the searched version
    this.getBrandConfigByVersion(this.searchQuery, (response: any) => {
      const isDataAvailable = (_.get(response, "data.code") == 0) ? true : false;
      if (isDataAvailable && response.data.brand_configuration && response.data.brand_configuration.length > 0) {
        const config = response.data.brand_configuration[0];
        const managerList = response.data.manager_list || [];
        // Create a single-item version list
        const versionData = {
          version: config.version,
          created_date: this.helpersService.getTimeByOffset(
            this.brandOffsetName["__zone_symbol__value"]['offset'],
            config.created_date,
            "DD-MMM-YY HH:mm"
          ),
          created_by: config.created_by,
          source_version: config.reference_version || '',
          status: config.version === this.currentPublishedVersion ? 'Live' : 'Draft'
        };
        if (_.get(config, 'created_by_email_address')) {
          versionData['created_by'] = _.get(response, 'data.brand_configuration.0.created_by_email_address');
        }
        const previewURL = `interaction/preview?project_id=${this.brandDetails['_id']}&version=${config['version']}`
        versionData['preview'] = '<a  href="' + previewURL + '" target="_blank" >Click Here</a>'
        // if (this.isProjectLevelConversationPreview) {
        //   const previewURL = this.conversationPreviewURL + '?config=' + config['version'];
        //   versionData['preview'] = '<a  href="' + previewURL + '" target="_blank" >Click Here</a>'
        // }
        // Update the version list with the single item
        this.versionList = [versionData];
        this.isDataAvailable = true;
        this.totalPages = 1;
        this.pageNumber = 1;
        this.totalVersionListCount = 1;
        this.hasNextPage = false;
        this.mapLiveConversationPublishManager(managerList);
      } else {
        this.errorMessage = 'Version not found';
        this.versionList = [];
        // this.isDataAvailable = false;
      }
      this.isTableLoading = false;
    });
  }

  clearSearch() {
    this.searchQuery = '';
    this.errorMessage = '';
    this.pageNumber = 1;
    this.isTableLoading = true;
    this.totalVersionListCount = 0;
    this.getBrandConversationConfigList('page_move');
  }

  onSearchQueryChange(value: string) {
  if (!value.trim()) {
    // If user cleared the input manually
    this.errorMessage = '';
    this.pageNumber = 1;
    this.selectedRow = null; // Optional: clear any selected row
    this.totalVersionListCount = 0
    this.getBrandConversationConfigList('page_move');
  }
}

goToFirstPage(): void {
  if (this.pageNumber !== 1) {
    this.pageNumber = 1;
    this.getBrandConversationConfigList('page_move');
  }
}

goToLastPage(): void {
  if (this.pageNumber !== this.totalPages) {
    this.pageNumber = this.totalPages;
    this.getBrandConversationConfigList('page_move');
  }
}
}
